import React, { memo, useCallback } from 'react'

import { format } from 'date-fns'
import { ConciergeBell } from 'lucide-react'
import Link from 'next/link'
import type { EventWrapperProps as RBCEventWrapperProps } from 'react-big-calendar'
import {
  HiLockClosed,
  HiOutlineClock,
  HiOutlineCurrencyDollar,
} from 'react-icons/hi2'
import { HiOutlineSquare3Stack3D } from 'react-icons/hi2'
import { LuListTodo } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import { EmptyValue } from '@/components/common/EmptyValue'
import type {
  CalendarEventObj,
  Event,
} from '@/components/common/EventCalendar/types'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { COLOR_CLASSNAMES } from '@/constants/color'

import { formatDollarAmountWithSuffix } from '@/helpers/string'
import { getTaskPath } from '@/helpers/task'

import { useOrganizationStore } from '@/stores/OrganizationStore'

import type { Color } from '@/types/Color'
import type { Pursuit } from '@/types/pursuit/Pursuit'
import type { Task } from '@/types/task/Task'

export interface EventWrapperProps extends RBCEventWrapperProps {
  event: Event
  onEventClick: (event: Event) => void
}

interface EventPreviewContentProps {
  previewData: CalendarEventObj
}

const TASK_STATUS_CONFIG: Record<string, { label: string; className: string }> =
  {
    todo: {
      label: 'To Do',
      className:
        'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300',
    },
    in_progress: {
      label: 'In Progress',
      className:
        'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    },
    in_review: {
      label: 'In Review',
      className:
        'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    },
    completed: {
      label: 'Completed',
      className:
        'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    },
    canceled: {
      label: 'Canceled',
      className: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    },
  }

const PRIORITY_CONFIG: Record<string, { label: string; className: string }> = {
  low: { label: 'Low', className: TASK_STATUS_CONFIG['todo'].className },
  medium: {
    label: 'Medium',
    className: TASK_STATUS_CONFIG['in_progress'].className,
  },
  high: { label: 'High', className: TASK_STATUS_CONFIG['canceled'].className },
}

interface TaskPreviewContentProps {
  task: Task
}

const TaskPreviewContent = memo(
  ({ task }: TaskPreviewContentProps) => {
    // TODO: why does using useActiveOrgId cause like 10-20 re-renders every time we hover over an event?
    // this prevents the tooltip from appearing so we're using the organization store instead
    const { activeOrgId } = useOrganizationStore()

    const statusConfig = TASK_STATUS_CONFIG[task.status] || {
      label: task.status,
      className: TASK_STATUS_CONFIG[task.status].className,
    }

    const path = getTaskPath(activeOrgId, task)

    return (
      <div className="w-[260px]">
        <div className="flex items-center justify-between mb-1">
          {task.is_request ? (
            <div className="flex items-center gap-1.5 text-[10px] font-medium text-accent-600 dark:text-accent-400 uppercase tracking-wide">
              <ConciergeBell size={12} />
              Concierge request
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[10px] font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wide">
              <LuListTodo size={12} />
              Task
            </div>
          )}
          <span className="flex items-center gap-1 text-[11px] text-primary-600 dark:text-primary-400">
            <HiOutlineClock size={12} />
            {task.due_date ? (
              format(new Date(task.due_date), 'MMM d')
            ) : (
              <EmptyValue label="No due date" />
            )}
          </span>
        </div>
        <Link
          href={path}
          className="font-medium text-[13px] text-primary-900 dark:text-primary-100 leading-tight hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        >
          <span className="block truncate">{task.title}</span>
        </Link>

        {task.description && (
          <p className="mt-1 text-xs text-primary-500 dark:text-primary-400 line-clamp-1 leading-relaxed">
            {task.description}
          </p>
        )}

        <div className="flex items-center gap-1.5 mt-2">
          <span
            className={twMerge(
              'px-2 py-0.5 rounded-full text-[11px] font-medium',
              statusConfig.className,
            )}
          >
            {statusConfig.label}
          </span>
          <span
            className={twMerge(
              'px-2 py-0.5 rounded-full text-[11px] font-medium',
              PRIORITY_CONFIG[task.priority].className,
            )}
          >
            {PRIORITY_CONFIG[task.priority].label}
          </span>
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    return prevProps.task.id === nextProps.task.id
  },
)

TaskPreviewContent.displayName = 'EventCalendar.EventWrapper.TaskPreviewContent'

interface PursuitPreviewContentProps {
  pursuit: Pursuit
}

const PursuitPreviewContent: React.FC<PursuitPreviewContentProps> = ({
  pursuit,
}) => {
  const stageColor = pursuit.stage?.color || 'zinc'
  const colorClassName =
    COLOR_CLASSNAMES[stageColor as Color] || COLOR_CLASSNAMES['zinc']

  return (
    <div className="w-[260px]">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-[10px] font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wide">
          <HiOutlineSquare3Stack3D size={12} />
          Pursuit
        </div>
        <div className="flex items-center gap-2 text-[11px] text-primary-600 dark:text-primary-400">
          <span className="flex items-center gap-1">
            <HiOutlineClock size={12} />
            {pursuit.due_date ? (
              format(new Date(pursuit.due_date), 'MMM d')
            ) : (
              <EmptyValue label="No due date" />
            )}
          </span>
          {pursuit.estimated_value !== null && (
            <span className="flex items-center gap-1">
              <HiOutlineCurrencyDollar size={12} />
              {formatDollarAmountWithSuffix(pursuit.estimated_value, 2)}
            </span>
          )}
        </div>
      </div>
      <Link
        href={`/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}`}
        className="font-medium text-[13px] text-primary-900 dark:text-primary-100 leading-tight hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        <span className="block truncate">{pursuit.title}</span>
      </Link>

      {pursuit.description && (
        <p className="mt-1 text-xs text-primary-500 dark:text-primary-400 line-clamp-1 leading-relaxed">
          {pursuit.description}
        </p>
      )}

      {pursuit.stage?.name && (
        <div className="flex items-center mt-2">
          <span
            className={twMerge(
              'inline-flex items-center justify-center px-2 h-6 rounded-full text-[11px] font-medium',
              colorClassName,
            )}
          >
            {pursuit.stage.name}
          </span>
        </div>
      )}
    </div>
  )
}

const EventPreviewContent: React.FC<EventPreviewContentProps> = ({
  previewData,
}) => {
  switch (previewData.type) {
    case 'task':
      return <TaskPreviewContent task={previewData.data} />
    case 'pursuit':
      return <PursuitPreviewContent pursuit={previewData.data} />
    default: {
      const _exhaustiveCheck: never = previewData
      return _exhaustiveCheck
    }
  }
}

const isConciergeRequest = (obj: any): boolean =>
  obj?.type === 'task' && obj.data?.is_request === true

// Same runtime caution as isConciergeRequest above: event.obj crosses
// react-big-calendar untyped, so don't trust `data` to be populated.
const isPrivatePursuitEvent = (obj: CalendarEventObj): boolean =>
  obj?.type === 'pursuit' && obj.data?.has_unrestricted_access === false

// Extract preview data from event object (handles both wrapped and raw formats)
const getPreviewData = (obj: CalendarEventObj): CalendarEventObj | null => {
  switch (obj.type) {
    case 'task':
      return obj
    case 'pursuit':
      return obj
    default:
      return null
  }
}

export const MonthViewEventWrapper: React.FC<EventWrapperProps> = ({
  event,
  onEventClick,
}) => {
  const handleClick = useCallback(() => {
    onEventClick(event)
  }, [event, onEventClick])

  const previewData = getPreviewData(event.obj)

  const buttonContent = (
    <Button
      onClick={handleClick}
      className={twMerge(
        'flex justify-start items-center gap-x-1 h-full w-full px-[2px] md:px-[4px] rounded-[3px] active:scale-100',
        event.className ?? '',
      )}
    >
      {/* Icons are flex siblings of the title (not inline in its line box) so
          the chip's items-center centers them against the single, line-clamped
          title line. */}
      {isConciergeRequest(event.obj) && (
        <ConciergeBell className="flex-shrink-0 h-3 w-3" />
      )}
      {isPrivatePursuitEvent(event.obj) && (
        <HiLockClosed
          role="img"
          aria-label="Private pursuit"
          className="flex-shrink-0 h-3 w-3"
        />
      )}
      <span className="min-w-0 text-xs font-medium line-clamp-1">
        {event.title}
      </span>
    </Button>
  )

  if (previewData) {
    return (
      <div className="flex items-center justify-center w-full">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
          <TooltipContent
            side="top"
            align="center"
            inverseColors
            className="p-3"
          >
            <EventPreviewContent previewData={previewData} />
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center w-full">
      {buttonContent}
    </div>
  )
}

export const AgendaViewEventWrapper: React.FC<EventWrapperProps> = ({
  event,
  onEventClick,
}) => {
  const handleClick = useCallback(() => {
    onEventClick(event)
  }, [event, onEventClick])

  return (
    <button
      onClick={handleClick}
      className="text-left hover:underline underline-offset-2"
    >
      {/* Title can wrap here, so icons stay inline and ride the first line;
          align-middle centers them on the text rather than dropping to the
          descender (align-text-bottom). */}
      {isConciergeRequest(event.obj) && (
        <ConciergeBell className="mr-1 inline-block h-3 w-3 align-middle" />
      )}
      {isPrivatePursuitEvent(event.obj) && (
        <HiLockClosed
          role="img"
          aria-label="Private pursuit"
          className="mr-1 inline-block h-3 w-3 align-middle"
        />
      )}
      {event.title}
    </button>
  )
}
